/**
 * GIELLE — Costo imballaggio per piccole quantità — v6.
 *
 * Regola:
 * - somma (mq_per_confezione × quantità) per le righe con tipo_vendita = "mq";
 * - 0 m²: nessun supplemento;
 * - > 0 e < soglia (25 m² di default): 1 × prodotto tecnico;
 * - >= soglia: rimuove il prodotto tecnico.
 *
 * v6: sincronizzazione resa resistente agli aggiornamenti asincroni di Horizon,
 * agli aumenti/riduzioni quantità e alla rimozione dell'ultimo prodotto a m².
 */
(function () {
  const CONFIG_ID = 'GiellePackagingFeeConfig';
  const STATE_SELECTOR = '[data-gielle-packaging-state]';
  const FEE_PROPERTY = '_gielle_packaging_fee';
  const SOURCE_ID = 'gielle-packaging-fee';

  let busy = false;
  let timer = 0;
  let verificationTimer = 0;
  let rerunRequested = false;
  let pendingHasOverride = false;
  let pendingTotalMq = null;
  let lastError = '';

  function getConfig() {
    const node = document.getElementById(CONFIG_ID);
    if (!node) return null;

    try {
      const config = JSON.parse(node.textContent || '{}');
      config.variantId = Number(config.variantId || 0);
      config.thresholdMq = Number(config.thresholdMq || 25);
      return config;
    } catch (error) {
      console.error('[Gielle packaging] Configurazione non valida', error);
      return null;
    }
  }

  function parseMqValue(value) {
    const parsed = Number.parseFloat(String(value ?? '0').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  function getTotalMq() {
    const states = Array.from(document.querySelectorAll(STATE_SELECTOR));
    if (states.length === 0) return null;

    // Preferiamo l'ultimo blocco disponibile: in Horizon il drawer può convivere
    // con altri frammenti di carrello nella pagina.
    for (let i = states.length - 1; i >= 0; i -= 1) {
      const value = parseMqValue(states[i].dataset.totalMq);
      if (value !== null) return value;
    }

    return null;
  }

  function getTotalMqFromSections(sections) {
    if (!sections || typeof sections !== 'object') return null;

    let found = null;
    for (const html of Object.values(sections)) {
      if (typeof html !== 'string' || !html.includes('data-gielle-packaging-state')) continue;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const states = Array.from(doc.querySelectorAll(STATE_SELECTOR));
      for (const state of states) {
        const value = parseMqValue(state.dataset.totalMq);
        if (value !== null) found = value;
      }
    }

    return found;
  }

  function getSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean)
      .filter((id, index, arr) => arr.indexOf(id) === index)
      .slice(0, 5);
  }

  function setCheckoutLocked(locked, message = '') {
    document.documentElement.classList.toggle('gielle-packaging-syncing', locked);

    for (const button of document.querySelectorAll('.cart__checkout-button')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      if (locked) {
        if (button.dataset.giellePackagingLocked !== 'true') {
          button.dataset.gielleWasDisabled = button.disabled ? 'true' : 'false';
          button.dataset.giellePackagingLocked = 'true';
        }
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else if (button.dataset.giellePackagingLocked === 'true') {
        if (button.dataset.gielleWasDisabled !== 'true') button.disabled = false;
        delete button.dataset.gielleWasDisabled;
        delete button.dataset.giellePackagingLocked;
        button.removeAttribute('aria-busy');
      }
    }

    for (const ctas of document.querySelectorAll('.additional-checkout-buttons')) {
      if (!(ctas instanceof HTMLElement)) continue;
      ctas.style.pointerEvents = locked ? 'none' : '';
      ctas.style.opacity = locked ? '0.55' : '';
      if (locked) ctas.setAttribute('aria-busy', 'true');
      else ctas.removeAttribute('aria-busy');
    }

    for (const status of document.querySelectorAll('[data-gielle-packaging-status]')) {
      status.textContent = locked ? (message || 'Aggiornamento costo imballaggio…') : '';
      status.toggleAttribute('hidden', !locked);
    }
  }

  async function getCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Cart fetch failed (${response.status})`);
    return response.json();
  }

  function isFeeItem(item, variantId) {
    return Number(item.variant_id) === variantId || item.properties?.[FEE_PROPERTY] === '1';
  }

  function feeItems(cart, variantId) {
    return (cart.items || []).filter((item) => isFeeItem(item, variantId));
  }

  function hasRealCartItems(cart, variantId) {
    return (cart.items || []).some((item) => !isFeeItem(item, variantId));
  }

  async function mutateCart(action, feeItem, config) {
    const root = window.Shopify?.routes?.root || '/';
    const sections = getSectionIds();
    const sectionParams = sections.length
      ? { sections: sections.join(','), sections_url: window.location.pathname + window.location.search }
      : {};

    let url;
    let payload;

    if (action === 'add') {
      url = `${root}cart/add.js`;
      payload = {
        items: [{
          id: config.variantId,
          quantity: 1,
          properties: { [FEE_PROPERTY]: '1' },
        }],
        ...sectionParams,
      };
    } else {
      url = `${root}cart/change.js`;
      payload = {
        id: feeItem.key,
        quantity: action === 'remove' ? 0 : 1,
        ...sectionParams,
      };
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || `Cart update failed (${response.status})`);
    }

    const cart = await getCart();
    const renderedSections = result.sections || {};

    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: SOURCE_ID,
        data: {
          source: SOURCE_ID,
          itemCount: cart.item_count || 0,
          sections: renderedSections,
        },
      },
    }));

    return cart;
  }

  function rememberOverride(value) {
    if (Number.isFinite(value)) {
      pendingTotalMq = Number(value);
      pendingHasOverride = true;
    }
  }

  function queueSync(delay = 0, totalMqOverride = null) {
    rememberOverride(totalMqOverride);

    if (busy) {
      // La vecchia versione poteva perdere un aggiornamento arrivato mentre una
      // sincronizzazione era in corso. Ora lo conserviamo e lo rieseguiamo.
      rerunRequested = true;
      return;
    }

    window.clearTimeout(timer);
    setCheckoutLocked(true);

    timer = window.setTimeout(() => {
      const hasOverride = pendingHasOverride;
      const override = pendingTotalMq;
      pendingHasOverride = false;
      pendingTotalMq = null;
      sync(hasOverride ? override : null);
    }, delay);
  }

  function queueVerification(delay = 650) {
    window.clearTimeout(verificationTimer);
    verificationTimer = window.setTimeout(() => queueSync(0), delay);
  }

  async function sync(totalMqOverride = null) {
    if (busy) {
      rememberOverride(totalMqOverride);
      rerunRequested = true;
      return;
    }

    const config = getConfig();
    if (!config?.enabled) {
      setCheckoutLocked(false);
      return;
    }

    if (!config.variantId) {
      const msg = 'Prodotto “Costo imballaggio per piccole quantità” non trovato. Selezionalo in Impostazioni tema → Gielle – Imballaggio piccole quantità.';
      if (lastError !== msg) console.warn(`[Gielle packaging] ${msg}`);
      lastError = msg;
      setCheckoutLocked(false);
      return;
    }

    busy = true;
    setCheckoutLocked(true);
    let changed = false;

    try {
      // Leggiamo PRIMA il carrello reale. Se è rimasto soltanto il prodotto tecnico,
      // il supplemento va rimosso sempre, anche se il DOM del drawer fosse ancora vecchio.
      const cart = await getCart();
      const fees = feeItems(cart, config.variantId);
      const hasRealItems = hasRealCartItems(cart, config.variantId);

      let totalMq = Number.isFinite(totalMqOverride) ? Number(totalMqOverride) : getTotalMq();
      if (!hasRealItems) totalMq = 0;

      // Se ci sono prodotti reali ma non abbiamo ancora un totale affidabile,
      // aspettiamo il rendering del carrello invece di prendere decisioni rischiose.
      if (totalMq === null) {
        lastError = '';
        return;
      }

      const needsFee = hasRealItems && totalMq > 0 && totalMq < config.thresholdMq;

      if (needsFee && fees.length === 0) {
        await mutateCart('add', null, config);
        changed = true;
        return;
      }

      if (needsFee && fees.length > 0) {
        const primary = fees[0];
        if (primary.quantity !== 1) {
          await mutateCart('set-one', primary, config);
          changed = true;
          return;
        }
        if (fees.length > 1) {
          await mutateCart('remove', fees[1], config);
          changed = true;
          return;
        }
      }

      if (!needsFee && fees.length > 0) {
        await mutateCart('remove', fees[0], config);
        changed = true;
        return;
      }

      lastError = '';
    } catch (error) {
      console.error('[Gielle packaging] Impossibile sincronizzare il supplemento', error);
      lastError = String(error?.message || error);
    } finally {
      busy = false;

      if (changed || rerunRequested || pendingHasOverride) {
        rerunRequested = false;
        queueSync(changed ? 120 : 20);
      } else {
        setCheckoutLocked(false);
      }
    }
  }

  // Evento principale di Horizon: contiene le sezioni appena renderizzate da Shopify.
  document.addEventListener('cart:update', (event) => {
    if (event.detail?.sourceId === SOURCE_ID || event.detail?.data?.source === SOURCE_ID) return;

    const freshTotalMq = getTotalMqFromSections(event.detail?.data?.sections);
    queueSync(20, freshTotalMq);
    // Secondo controllo a DOM ormai stabilizzato: copre anche integrazioni/app che
    // emettono cart:update senza includere le sezioni HTML.
    queueVerification(700);
  });

  // Fallback specifico per quantità e cestino. Questi eventi/click avvengono prima
  // della risposta Ajax di Horizon, quindi aspettiamo che il carrello sia aggiornato.
  document.addEventListener('quantity-selector:update', () => queueVerification(750));
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('.cart-items__remove')) queueVerification(850);
  }, true);

  // Se Horizon sostituisce il frammento del carrello senza un evento utilizzabile,
  // intercettiamo esclusivamente la comparsa/modifica del nostro stato m².
  const observer = new MutationObserver((mutations) => {
    let relevant = false;

    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.target instanceof Element && mutation.target.matches(STATE_SELECTOR)) {
        relevant = true;
        break;
      }

      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(STATE_SELECTOR) || node.querySelector?.(STATE_SELECTOR)) {
          relevant = true;
          break;
        }
      }
      if (relevant) break;
    }

    if (relevant) queueSync(80);
  });

  function startObserver() {
    if (!document.body) return;
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-total-mq'],
    });
  }

  window.addEventListener('pageshow', () => queueSync(0));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      startObserver();
      queueSync(0);
    }, { once: true });
  } else {
    startObserver();
    queueSync(0);
  }
})();
